import React, { FC, useRef, useCallback, Dispatch, useState } from 'react';
import styled from '@emotion/styled';

const SWITCH_WIDTH = 60;
const SWITCH_HEIGHT = 30;
const KNOB_DIAMETER = 20;

const _ToggleButton = styled.button`
  &[aria-pressed] {
    display: flex;
    box-sizing: border-box;
    border: none;
    position: relative;
    width: ${SWITCH_WIDTH}px;
    height: ${SWITCH_HEIGHT}px;
    align-items: center;
    justify-content: space-between;
    padding: ${SWITCH_HEIGHT / 2}px;
    margin-left: 0.75rem;
    margin-right: 0.5rem;
  }

  &[aria-pressed][disabled],
  &[aria-pressed][disabled]:hover {
    color: #999;
  }

  &[aria-pressed]:focus,
  &[aria-pressed]:hover {
    color: #00f;
    outline: none;
  }

  &[aria-pressed]:focus::before,
  &[aria-pressed]:hover::before {
    box-shadow: 0 3px 7px rgba(0, 0, 0, 0.25);
  }

  &[aria-pressed]:focus::after,
  &[aria-pressed]:hover::after {
    background-size: 30%;
    background-repeat: no-repeat;
    background-position: center center;
  }

  &[aria-pressed]::before,
  &[aria-pressed]::after {
    content: '';
    position: absolute;
    height: ${KNOB_DIAMETER}px;
    transition: all 0.25s ease;
  }

  &[aria-pressed]::before {
    left: 0;
    top: 0;

    width: ${SWITCH_WIDTH}px;
    border: ${SWITCH_HEIGHT / 2}px solid #767676;
    background: #767676;
    border-radius: 1.1em;
  }

  &[aria-pressed]::after {
    left: ${KNOB_DIAMETER / 4}px;
    top: ${KNOB_DIAMETER / 4}px;
    background-color: #fff;
    background-position: center center;
    border-radius: 50%;
    width: ${KNOB_DIAMETER}px;
    box-shadow: 0 3px 7px rgba(0, 0, 0, 0.25);
  }

  &[aria-pressed='true']::after {
    left: ${SWITCH_WIDTH - KNOB_DIAMETER * 1.25}px;
    border-color: #7bb801;
    color: #7bb801;
  }

  &[aria-pressed='true']::before {
    background-color: #7bb801;
    border-color: #7bb801;
  }

  &[aria-pressed][disabled]::before {
    background-color: transparent;
    border-color: #ddd;
  }

  &[aria-pressed][disabled]::after {
    border-color: #ddd;
  }

  &[aria-pressed][disabled]:hover {
    color: #999; /* case for CSS custom property if not supporting IE/Edge */
  }

  &[aria-pressed][disabled]:hover::before {
    box-shadow: none;
  }

  &[aria-pressed][disabled]:hover::after {
    background-image: none;
  }
`;

const _FlippedToggle = styled(_ToggleButton)`
  &[aria-pressed]::before,
  &[aria-pressed]::after {
    left: auto;
    right: 0;
  }

  &[aria-pressed]::after {
    left: auto;
    right: ${SWITCH_WIDTH - KNOB_DIAMETER * 1.25}px;
  }

  &[aria-pressed='true']::after {
    right: ${KNOB_DIAMETER / 4}px;
  }

  &[aria-pressed] {
    padding-left: 0;
    padding-right: 4em;
  }
`;

const ToggleButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #566072;
  min-height: ${SWITCH_HEIGHT}px;
`;
const IndicatorWrapper = styled.span`
  font-weight: 600;
  font-size: 12px;
`;
export type ToggleButtonProps =
  | {
      active: boolean;
      showIndicator?: boolean;
      onActivate: Dispatch<void>;
      onDeactivate: Dispatch<void>;
    }
  | {
      active: boolean;
      showIndicator?: boolean;
      onToggle: Dispatch<void>;
    };

export const ToggleButton: FC<ToggleButtonProps> = props => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const onClick = useCallback(() => {
    if (btnRef.current) {
      if ('onToggle' in props) {
        props.onToggle();
      } else {
        if (!props.active) {
          props.onActivate();
        }
        if (props.active) {
          props.onDeactivate();
        }
      }
      btnRef.current.blur();
    }
  }, [props]);

  return (
    <ToggleButtonWrapper>
      {props.children}
      <_ToggleButton
        ref={btnRef}
        aria-pressed={props.active ? 'true' : 'false'}
        onClick={onClick}
      />

      {props.showIndicator && <IndicatorWrapper>{props.active ? 'On' : 'Off'}</IndicatorWrapper>}
    </ToggleButtonWrapper>
  );
};
